import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatCAD } from '../utils/currency';
import { 
  CreditCard, 
  Loader2, 
  CheckCircle,
  ArrowLeft,
  DollarSign,
  Heart,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];

export default function TopUpPage() {
  const { t, language } = useLanguage();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Contribution state
  const [contributionConfig, setContributionConfig] = useState(null);
  const [includeContribution, setIncludeContribution] = useState(true);
  const [customContribution, setCustomContribution] = useState('');
  const [useCustomContribution, setUseCustomContribution] = useState(false);

  // Fetch contribution config on mount
  useEffect(() => {
    const fetchContributionConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/contribution-config`);
        const data = await response.json();
        setContributionConfig(data);
        // Set custom contribution mode based on admin config
        if (data.custom_amount_default) {
          setUseCustomContribution(true);
        }
      } catch (error) {
        logger.error('Error fetching contribution config:', error);
        // Use defaults
        setContributionConfig({
          enabled: true,
          default_percentage: 5,
          custom_amount_allowed: true,
          custom_amount_default: false,
          min_custom_amount: 0.5,
          max_custom_amount: 100,
          contribution_message_fr: "Contribution volontaire au Hub Québec+",
          contribution_message_en: "Voluntary contribution to Hub Québec+"
        });
      }
    };
    fetchContributionConfig();
  }, []);

  // Calculate contribution amount
  const calculateContribution = () => {
    if (!includeContribution || !contributionConfig?.enabled) return 0;
    
    if (useCustomContribution && customContribution) {
      return parseFloat(customContribution) || 0;
    }
    
    if (contributionConfig.default_type === 'percent') {
      return Math.round((amount * contributionConfig.default_percent / 100) * 100) / 100;
    }
    return contributionConfig.default_amount || 1.0;
  };

  const contributionAmount = calculateContribution();
  const totalAmount = amount + contributionAmount;

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your transactions.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await api.get(`/payments/topup/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setSuccess(true);
        setCheckingPayment(false);
        toast.success(t('topup.success'));
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      logger.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [api, t]);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 5) {
      setAmount(num);
    }
    setCustomAmount(value);
  };

  const handleSubmit = async () => {
    if (amount < 5) {
      toast.error(t('topup.min_amount'));
      return;
    }

    setLoading(true);
    try {
      if (paymentMethod === 'stripe') {
        const response = await api.post('/payments/topup/stripe', {
          amount: parseFloat(amount),
          contribution_amount: contributionAmount,
          include_contribution: includeContribution && contributionAmount > 0,
          payment_method: 'stripe',
          origin_url: window.location.origin
        });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else if (paymentMethod === 'paypal') {
        toast.info('PayPal integration coming soon!');
      } else if (paymentMethod === 'interac') {
        toast.info('Interac e-Transfer integration coming soon!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card className="text-center animate-fade-in-up">
            <CardContent className="pt-12 pb-8">
              <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('topup.success')}</h2>
              <p className="text-muted-foreground mb-4">{t('topup.success_message')}</p>
              {contributionAmount > 0 && (
                <p className="text-sm text-blue-600 mb-6">
                  {language === 'fr' 
                    ? `Merci pour votre contribution de ${formatCAD(contributionAmount)}!` 
                    : `Thank you for your ${formatCAD(contributionAmount)} contribution!`}
                </p>
              )}
              <Button onClick={() => navigate('/dashboard')} className="rounded-full px-8" data-testid="back-to-dashboard">
                {t('topup.back_to_dashboard')}
              </Button>
            </CardContent>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card className="text-center animate-fade-in-up">
            <CardContent className="pt-12 pb-8">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-6 text-primary" />
              <h2 className="text-xl font-bold mb-2">{t('topup.processing')}</h2>
              <p className="text-muted-foreground">Verifying your payment...</p>
            </CardContent>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      <main className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('dashboard.welcome').split(',')[0]}
        </Button>

        <Card className="animate-fade-in-up">
          <CardHeader>
            <CardTitle>{t('topup.title')}</CardTitle>
            <CardDescription>{t('topup.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Amount Selection */}
            <div className="space-y-3">
              <Label>{t('topup.amount')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {PRESET_AMOUNTS.map((preset) => (
                  <Button
                    key={preset}
                    type="button"
                    variant={amount === preset && !customAmount ? 'default' : 'outline'}
                    className="rounded-xl h-12"
                    onClick={() => handleAmountSelect(preset)}
                    data-testid={`amount-${preset}`}
                  >
                    {preset}$
                  </Button>
                ))}
              </div>
              
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder={t('topup.custom_amount')}
                  className="pl-10 h-12 rounded-xl"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  min="5"
                  step="0.01"
                  data-testid="custom-amount"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('topup.min_amount')}</p>
            </div>

            {/* Voluntary Contribution Section */}
            {contributionConfig?.enabled && (
              <div className="space-y-3 p-4 rounded-xl border-2 border-blue-200 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="include-contribution"
                    checked={includeContribution}
                    onCheckedChange={setIncludeContribution}
                    className="mt-1"
                    data-testid="contribution-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="include-contribution" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Heart className="h-4 w-4 text-green-500 fill-green-500" />
                        <span className="font-semibold text-blue-900 dark:text-blue-100">
                          {(contributionConfig.title?.[language] || contributionConfig.title?.fr || '').replace(/💙\s*/g, '')}
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {contributionConfig.description?.[language] || contributionConfig.description?.fr}
                      </p>
                    </Label>
                  </div>
                </div>
                
                {includeContribution && (
                  <div className="mt-3 pl-7 space-y-3">
                    {/* Default contribution display */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-700 dark:text-blue-300">
                        {contributionConfig.default_type === 'percent' 
                          ? `${contributionConfig.default_percent}% ${language === 'fr' ? 'de votre recharge' : 'of your top-up'}`
                          : `${language === 'fr' ? 'Montant suggéré' : 'Suggested amount'}`
                        }
                      </span>
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        {formatCAD(calculateContribution())}
                      </span>
                    </div>
                    
                    {/* Custom contribution option */}
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="custom-contribution"
                        checked={useCustomContribution}
                        onCheckedChange={setUseCustomContribution}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="custom-contribution" className="text-sm text-blue-700 dark:text-blue-300 cursor-pointer">
                        {language === 'fr' ? 'Montant personnalisé' : 'Custom amount'}
                      </Label>
                    </div>
                    
                    {useCustomContribution && (
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500" />
                        <Input
                          type="number"
                          placeholder={language === 'fr' ? 'Votre contribution' : 'Your contribution'}
                          className="pl-10 h-10 rounded-lg border-blue-300 bg-white dark:bg-blue-900/20"
                          value={customContribution}
                          onChange={(e) => setCustomContribution(e.target.value)}
                          min="0"
                          step="0.50"
                          data-testid="custom-contribution-input"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Payment Method */}
            <div className="space-y-3">
              <Label>{t('topup.payment_method')}</Label>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                <div 
                  className={`flex items-center space-x-3 p-4 rounded-xl border transition-all cursor-pointer ${
                    paymentMethod === 'stripe' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setPaymentMethod('stripe')}
                >
                  <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                  <Label htmlFor="stripe" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="h-10 w-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-[#635BFF]" />
                    </div>
                    <div>
                      <p className="font-medium">{t('topup.stripe')}</p>
                      <p className="text-xs text-muted-foreground">Visa, Mastercard, Amex</p>
                    </div>
                  </Label>
                </div>

                <div 
                  className={`flex items-center space-x-3 p-4 rounded-xl border transition-all cursor-pointer ${
                    paymentMethod === 'paypal' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setPaymentMethod('paypal')}
                >
                  <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                  <Label htmlFor="paypal" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="h-10 w-10 rounded-lg bg-[#003087]/10 flex items-center justify-center">
                      <span className="text-[#003087] font-bold text-sm">PP</span>
                    </div>
                    <div>
                      <p className="font-medium">{t('topup.paypal')}</p>
                      <p className="text-xs text-muted-foreground">PayPal Account</p>
                    </div>
                  </Label>
                </div>

                <div 
                  className={`flex items-center space-x-3 p-4 rounded-xl border transition-all cursor-pointer ${
                    paymentMethod === 'interac' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setPaymentMethod('interac')}
                >
                  <RadioGroupItem value="interac" id="interac" data-testid="payment-interac" />
                  <Label htmlFor="interac" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="h-10 w-10 rounded-lg bg-[#FFBE00]/10 flex items-center justify-center">
                      <span className="text-[#FFBE00] font-bold text-sm">INT</span>
                    </div>
                    <div>
                      <p className="font-medium">{t('topup.interac')}</p>
                      <p className="text-xs text-muted-foreground">Canadian Banks</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Summary */}
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  {language === 'fr' ? 'Recharge' : 'Top-up'}
                </span>
                <span className="font-medium">{formatCAD(amount)}</span>
              </div>
              
              {includeContribution && contributionAmount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-blue-600 flex items-center gap-1">
                    <Heart className="h-3 w-3" />
                    {language === 'fr' ? 'Contribution' : 'Contribution'}
                  </span>
                  <span className="font-medium text-blue-600">+{formatCAD(contributionAmount)}</span>
                </div>
              )}
              
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total</span>
                  <span className="text-2xl font-bold">{formatCAD(totalAmount)}</span>
                </div>
              </div>
            </div>

            <Button 
              className="w-full h-12 rounded-xl" 
              onClick={handleSubmit}
              disabled={loading || amount < 5}
              data-testid="topup-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('topup.processing')}
                </>
              ) : (
                t('topup.continue')
              )}
            </Button>
          </CardContent>
        </Card>
      </main>

      <MobileNav />
    </div>
  );
}
